import { createElement, FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { isEmpty, size } from 'lodash';
import { createBemBlockBuilder, ShowMore, Truncate } from '@ebsco-ui/ebsco-ui';

import {
  AccessOptions,
  CoverImageContainer,
  PublicationInfo,
  RecordsTools,
  RecordSummaryAvailability,
  SEARCH_OPTION,
  ShowMoreButton,
  SummaryPlacement,
} from '@app/components';
import { useRecordSummaryContext, useSearchContext } from '@app/contexts';
import {
  useCoverArtFetch,
  useInstanceDetailsRoutePath,
  useQueryParams,
} from '@app/hooks';
import { formatArrayToString, generateRouteParams } from '@app/utils';
import { DefaultCoverImageTypes, ROUTE } from '@app/constants';

import { RecordDataItem } from '../../RecordDataItem';
import { ParentField } from './ParentField';
import { Contributors } from '../../Contributors';

import '../RecordSummary.scss';

interface RecordSummaryCardProps {
  isModal?: boolean;
  isRTACShown?: boolean;
}

type PlacementBemBlocks = Record<
  SummaryPlacement,
  ReturnType<typeof createBemBlockBuilder>
>;

const cnBemRecordSummary = createBemBlockBuilder(['recordSummary']);
const cnBemModal = createBemBlockBuilder(['recordSummaryModal']);
const cnBemResults = createBemBlockBuilder(['recordSummaryResults']);
const cnBemDetails = createBemBlockBuilder(['recordSummaryInstanceDetails']);
const cnBemHoldsAndCheckouts = createBemBlockBuilder([
  'recordSummaryHoldsAndCheckouts',
]);

const placementBemBlocks: PlacementBemBlocks = {
  results: cnBemResults,
  instanceDetails: cnBemDetails,
  holdsAndCheckouts: cnBemHoldsAndCheckouts,
};

export const RecordSummaryCard: FC<RecordSummaryCardProps> = ({
  isModal = false,
  isRTACShown = true,
}) => {
  const {
    instance,
    itemStatusData,
    placement,
    testId,
    isInstanceDetails,
    isMobileHoldsAndCheckouts,
    isHoldsAndCheckouts,
    isMobileInstanceDetails,
    getItemStatusPanel,
  } = useRecordSummaryContext();
  const { data: coverArtSrc, isInitialLoading: isCoverArtLoading } =
    useCoverArtFetch(instance.isbns);
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const { setInstanceIdScrollTo } = useSearchContext();

  const cnBemModalOrPlacement = useMemo(
    () => (isModal ? cnBemModal : placementBemBlocks[placement]),
    [isModal, placement]
  );

  const cnBemWithCommon = (className: string) =>
    cn(cnBemRecordSummary(className), cnBemModalOrPlacement(className));

  const maxAmountToDisplay = 3;
  const { $t } = useIntl();

  const isNonCatalogItem = Boolean(itemStatusData?.isNonCatalogItem);
  const isVirtualItem =
    isHoldsAndCheckouts && Boolean(itemStatusData?.isVirtualItem);
  const sourceTypes = formatArrayToString(instance?.sourceTypes);

  const queryParams = useQueryParams();

  const getCardTitle = () => {
    const cardTitle =
      !isInstanceDetails && !isNonCatalogItem && !isVirtualItem ? (
        <Link
          data-link={`${instance.id}-link`}
          data-auto={`${instance.id}-link`}
          tabIndex={0}
          to={getInstanceDetailsRoutePath(instance.id)}
          onClick={() => setInstanceIdScrollTo(instance.id)}
        >
          {instance.title}
        </Link>
      ) : (
        instance.title
      );
    const modalTitle = (
      <h3 className={cn(cnBemModal('__contentWrapper-title'))}>
        <Truncate maxLines={2} content={instance.title} />
      </h3>
    );
    const titleLevel: Record<SummaryPlacement, number> = {
      results: 2,
      instanceDetails: 1,
      holdsAndCheckouts: 2,
    };

    return isModal
      ? modalTitle
      : createElement(
          `h${titleLevel[placement]}`,
          {
            className: cn(
              cnBemRecordSummary('__metadata-title'),
              cnBemModalOrPlacement('__contentWrapper-title')
            ),
            dir: 'ltr',
            'data-auto': 'record-summary-title',
          },
          cardTitle
        );
  };

  const isResults = placement === 'results';

  const getSubjects = () => {
    const areSubjectsHidden =
      isEmpty(instance.subjects) || !isResults || isModal;

    if (areSubjectsHidden) {
      return null;
    }

    return (
      <RecordDataItem
        label={$t({
          id: 'resultsList.subjects',
          defaultMessage: 'Subjects:',
        })}
        data-auto="subjects"
      >
        <ShowMore
          limit={maxAmountToDisplay}
          className={cnBemRecordSummary('__showMore')}
        >
          <ul className={cnBemRecordSummary('__contributorsList')}>
            {instance.subjects && (
              <ShowMore.Items>
                {instance.subjects.map(subject => {
                  const value = subject?.value ?? subject;

                  return (
                    <li key={value}>
                      <Link
                        to={generateRouteParams({
                          pathname: ROUTE.search,
                          query: {
                            ...queryParams,
                            option: SEARCH_OPTION.subject,
                            query: value,
                          },
                        })}
                        className={cn(
                          'underlinedLink',
                          cnBemModalOrPlacement('__link')
                        )}
                      >
                        {value}
                      </Link>
                    </li>
                  );
                })}
              </ShowMore.Items>
            )}
          </ul>
          <ShowMoreButton
            showMoreLimit={maxAmountToDisplay}
            dataLength={size(instance.subjects)}
          />
        </ShowMore>
      </RecordDataItem>
    );
  };

  const isEditionPresent = !isEmpty(instance.editions) && isResults && !isModal;

  return (
    <div
      data-auto={testId}
      className={cn(cnBemRecordSummary(), cnBemModalOrPlacement())}
    >
      <div className={cnBemWithCommon('__contentWrapper')}>
        <CoverImageContainer
          src={coverArtSrc}
          sourceTypes={
            isNonCatalogItem ? DefaultCoverImageTypes.nonCatalog : sourceTypes
          }
          isLoading={isCoverArtLoading}
          imageClassName={cnBemWithCommon('__coverImage')}
          className={cnBemWithCommon('__defaultImageWrapper')}
        />
        <div className={cnBemRecordSummary('__metadata')}>
          {sourceTypes && (
            <div
              className={cn(
                cnBemModalOrPlacement('__type'),
                cnBemWithCommon('__metadata-type')
              )}
              data-auto="source-types"
            >
              {sourceTypes}
            </div>
          )}
          {getCardTitle()}
          <div className={cnBemRecordSummary('__recordSection')}>
            <Contributors
              contributors={instance.contributors}
              isModal={isModal}
              cnBemModalOrPlacement={cnBemModalOrPlacement}
              isVirtualItem={isVirtualItem}
            />
            <ParentField />
            <PublicationInfo publication={instance.publication} />
            {isEditionPresent && (
              <RecordDataItem
                label={$t({
                  id: 'resultsList.edition',
                  defaultMessage: 'Edition:',
                })}
                data-auto="edition"
              >
                {instance.editions?.join(', ')}
              </RecordDataItem>
            )}
            {getSubjects()}
          </div>
          {!isModal && isRTACShown && (
            <>
              <div className={cnBemRecordSummary('__recordSection')}>
                <RecordSummaryAvailability />
              </div>
              {!isMobileInstanceDetails && !isHoldsAndCheckouts && (
                <AccessOptions />
              )}
            </>
          )}
        </div>
      </div>
      {!isModal && (
        <div className={cnBemRecordSummary('__toolsWrapper')}>
          <RecordsTools entry={instance} />
        </div>
      )}
      {isMobileInstanceDetails && !isModal && (
        <AccessOptions className={cnBemModalOrPlacement('__buttonsWrapper')} />
      )}
      {isMobileHoldsAndCheckouts &&
        itemStatusData &&
        !isModal &&
        getItemStatusPanel()}
    </div>
  );
};
